import { logger } from "@/core/logger";
import { Platform } from "react-native";

// Try to import expo-crypto for React Native (preferred)
let ExpoCrypto: any;
try {
  ExpoCrypto = require("expo-crypto");
} catch (e) {
  ExpoCrypto = null;
}

/**
 * Helper to read the HMAC secret for payments.
 *
 * On the frontend we can only safely access env vars that start with EXPO_PUBLIC_.
 * We still fall back to PAYMENT_HMAC_SECRET so this utility can be shared in
 * non-Expo environments (e.g. backend), but on mobile/web you should configure
 * EXPO_PUBLIC_PAYMENT_HMAC_SECRET in app.config / env.
 */
function getPaymentHmacSecret(): string {
  // Expo / React Native (public env)
  const expoPublicSecret = process.env.EXPO_PUBLIC_PAYMENT_HMAC_SECRET;
  if (expoPublicSecret) {
    return expoPublicSecret;
  }

  // Backend / Node (non-public env) – useful if this file is shared
  const backendSecret = process.env.PAYMENT_HMAC_SECRET;
  if (backendSecret) {
    return backendSecret;
  }

  return "";
}


function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i]!.toString(16).padStart(2, "0");
  }
  return hex;
}

/**
 * HMAC-SHA256 implementation using expo-crypto's digestStringAsync
 * HMAC algorithm: SHA-256(outer_pad || SHA-256(inner_pad || message))
 */
async function hmacSha256WithExpoCrypto(
  secret: string,
  message: string
): Promise<string> {
  if (!ExpoCrypto) {
    throw new Error("expo-crypto is not available");
  }

  const blockSize = 64; // SHA-256 block size in bytes

  // Convert secret to bytes
  const secretBytes = new TextEncoder().encode(secret);

  // If key is longer than block size, hash it
  let keyBytes: Uint8Array;
  if (secretBytes.length > blockSize) {
    const keyHash = await ExpoCrypto.digest(
      ExpoCrypto.CryptoDigestAlgorithm.SHA256,
      secretBytes
    );
    keyBytes = new Uint8Array(keyHash);
  } else {
    keyBytes = secretBytes;
  }

  // Pad key to block size with zeros
  const paddedKey = new Uint8Array(blockSize);
  paddedKey.set(keyBytes);

  // Create inner and outer padding
  const ipad = new Uint8Array(blockSize);
  const opad = new Uint8Array(blockSize);
  for (let i = 0; i < blockSize; i++) {
    ipad[i] = paddedKey[i]! ^ 0x36;
    opad[i] = paddedKey[i]! ^ 0x5c;
  }

  // Inner hash: SHA-256(ipad || message)
  const messageBytes = new TextEncoder().encode(message);
  const innerData = new Uint8Array(blockSize + messageBytes.length);
  innerData.set(ipad, 0);
  innerData.set(messageBytes, blockSize);
  const innerHash = await ExpoCrypto.digest(
    ExpoCrypto.CryptoDigestAlgorithm.SHA256,
    innerData
  );
  const innerHashBytes = new Uint8Array(innerHash);

  // Outer hash: SHA-256(opad || innerHash)
  const outerData = new Uint8Array(blockSize + 32);
  outerData.set(opad, 0);
  outerData.set(innerHashBytes, blockSize);
  const outerHash = await ExpoCrypto.digest(
    ExpoCrypto.CryptoDigestAlgorithm.SHA256,
    outerData
  );

  // Convert to hex
  return bufferToHex(outerHash);
}

/**
 * Low-level HMAC-SHA256 signature generator.
 * Returns a hex-encoded signature string.
 * Uses expo-crypto which supports web, iOS, and Android.
 * Throws error if expo-crypto is not available.
 */
export async function generateSignature(
  secret: string,
  payload: string
): Promise<string> {
  if (!secret) {
    const error = new Error("HMAC secret is missing");
    logger.error("hmac:generateSignature-missing-secret", { error: String(error) });
    throw error;
  }

  if (!ExpoCrypto) {
    const error = new Error(
      `HMAC generation failed: expo-crypto is not available. ` +
      `Platform: ${Platform.OS}. ` +
      `Please install expo-crypto: npx expo install expo-crypto`
    );
    logger.error("hmac:generateSignature-no-crypto", {
      hasExpoCrypto: false,
      platform: Platform.OS,
      error: String(error),
    });
    throw error;
  }

  try {
    logger.debug("hmac:using-expo-crypto", { platform: Platform.OS });
    return await hmacSha256WithExpoCrypto(secret, payload);
  } catch (error) {
    logger.error("hmac:generateSignature-failed", { error: String(error) });
    throw new Error(`HMAC generation failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Low-level HMAC-SHA256 signature verifier.
 */
export async function verifySignature(
  secret: string,
  payload: string,
  signature: string
): Promise<boolean> {
  if (!secret) {
    logger.warn("hmac:verifySignature-missing-secret");
    return false;
  }

  if (!signature) {
    return false;
  }

  const expected = await generateSignature(secret, payload);
  if (!expected) {
    // If we could not generate a signature (no crypto), fail closed
    return false;
  }

  // Simple constant-time-ish comparison
  if (expected.length !== signature.length) {
    return false;
  }

  let isEqual = 0;
  for (let i = 0; i < expected.length; i++) {
    isEqual |= expected.charCodeAt(i) ^ signature.charCodeAt(i);
  }
  return isEqual === 0;
}


export async function generatePaymentHmacSignature(
  payload: string
): Promise<string> {
  const hmacSecret = getPaymentHmacSecret();
  const hmacSignature = await generateSignature(hmacSecret, payload);
  return hmacSignature;
}

/**
 * High-level helper used for payment HMAC verification.
 * Mirrors the backend utility:
 *
 *   export function verifyPaymentHmacSignature(payload: string, signature: string): boolean { ... }
 */
export async function verifyPaymentHmacSignature(
  payload: string,
  signature: string
): Promise<boolean> {
  const hmacSecret = getPaymentHmacSecret();

  if (!hmacSecret) {
    throw new Error("PAYMENT_HMAC_SECRET is not configured");
  }

  return verifySignature(hmacSecret, payload, signature);
}
